/** Detección EN VIVO de ranks repetidos en el editor del paso 6 (Ranking).
 *
 * Bug de prod 2026-07-22: el admin guardó dos jugadores con rank 11 y dos
 * con 12 en la misma categoría y el draw de eliminación trató 14 posiciones
 * en vez de 16. El frontend resalta los inputs repetidos y bloquea el
 * guardado; el backend además rechaza con 409 (candado doble).
 *
 * S/R (rank null) es un estado válido y NUNCA cuenta como duplicado.
 */

export interface RankEntry {
  id: number;
  name: string;
  rank: number | null;
}

export interface DuplicateRankGroup {
  rank: number;
  names: string[];
}

/** Ranks no-nulos que aparecen más de una vez, con los nombres implicados. */
export function findDuplicateRanks(entries: RankEntry[]): DuplicateRankGroup[] {
  const byRank = new Map<number, string[]>();
  for (const entry of entries) {
    if (entry.rank == null) continue;
    const names = byRank.get(entry.rank);
    if (names) {
      names.push(entry.name);
    } else {
      byRank.set(entry.rank, [entry.name]);
    }
  }
  return [...byRank.entries()]
    .filter(([, names]) => names.length > 1)
    .sort(([a], [b]) => a - b)
    .map(([rank, names]) => ({ rank, names }));
}

/** "Rank repetido: 11 (Ian Cory, Isaac Zonana) · 12 (A, B)" */
export function formatDuplicateRanksMessage(groups: DuplicateRankGroup[]): string {
  if (groups.length === 0) return "";
  const detail = groups
    .map((group) => `${group.rank} (${group.names.join(", ")})`)
    .join(" · ");
  return `Rank repetido: ${detail}`;
}

/** Huecos en la numeración 1..max entre los ranks asignados.
 *
 * Decisión (2026-07-22): los huecos NO bloquean el guardado — S/R es válido
 * y hay formatos donde el admin numera solo a los sembrados — pero se avisa
 * con un warning no bloqueante porque un hueco suele delatar una captura a
 * medias (el síntoma hermano del bug de duplicados).
 */
export function findRankGaps(entries: RankEntry[]): number[] {
  const assigned = new Set<number>();
  let max = 0;
  for (const entry of entries) {
    if (entry.rank == null) continue;
    assigned.add(entry.rank);
    if (entry.rank > max) max = entry.rank;
  }
  const gaps: number[] = [];
  for (let rank = 1; rank < max; rank += 1) {
    if (!assigned.has(rank)) gaps.push(rank);
  }
  return gaps;
}
